// frontend/src/components/layout/navMenus.js
//
// The app's two menus as data: the sidebar (Sidebar.jsx) and the user menu
// under the avatar chip (UserDropdown.jsx). The phone's More sheet lists the
// same screens in the same groups and order (mobile/mobileNav.js), so all
// three read them from here.

import { ShoppingCart, ChartBar, GearSix } from '../../icons';
import { billingOffered } from '../billing/billingOffered';

/**
 * Top to bottom: a page link, or a collapsible section of links with its own
 * glyph. A section's `hub` is its landing screen, which no menu links (the
 * command palette does); the section counts as current there too.
 */
const SIDEBAR = [
  { path: '/', labelKey: 'nav.home' },
  { path: '/forecast', labelKey: 'nav.forecast', needsForecast: true },
  { path: '/promotions', labelKey: 'nav.promotions' },
  { path: '/inventory', labelKey: 'nav.inventory' },
  {
    id: 'purchase',
    labelKey: 'nav.purchase',
    icon: ShoppingCart,
    links: [
      { path: '/purchase-recommendations', labelKey: 'nav.purchaseCreate' },
      { path: '/purchase-orders', labelKey: 'nav.purchaseManage' },
    ],
  },
  {
    id: 'reports',
    labelKey: 'nav.reports',
    icon: ChartBar,
    hub: '/reports',
    links: [
      { path: '/reports/sales-forecast-accuracy', labelKey: 'nav.reportSalesForecastAccuracy' },
      { path: '/reports/stockout', labelKey: 'nav.reportStockout' },
      { path: '/reports/excess-stock', labelKey: 'nav.reportExcess' },
      { path: '/reports/classification', labelKey: 'nav.reportClassification' },
      { path: '/reports/gmroi', labelKey: 'nav.reportGmroi' },
      { path: '/reports/dead-stock', labelKey: 'nav.reportDeadStock' },
    ],
  },
  {
    id: 'settings',
    labelKey: 'nav.settings',
    icon: GearSix,
    links: [
      { path: '/item-setup', labelKey: 'nav.items' },
      { path: '/supplier-setup', labelKey: 'nav.suppliers' },
      { path: '/locations-setup', labelKey: 'nav.locations' },
    ],
  },
];

/**
 * The user menu: the store's profile and its other screens, the user's
 * profile, then the tools. Each profile is its own entry because the phone's
 * More sheet opens it from the store's or the user's card.
 */
const USER = {
  store: {
    profile: { path: '/store-settings', labelKey: 'nav.storeSettings' },
    links: [
      { path: '/plans', labelKey: 'nav.plans', needsBilling: true },
    ],
  },
  userProfile: { path: '/user-profile', labelKey: 'nav.userProfile' },
  tools: [
    { path: '/customer-orders', labelKey: 'nav.customerOrders', staffOnly: true },
    { path: '/data-health', labelKey: 'nav.dataHealth' },
    { path: '/exports', labelKey: 'nav.exports' },
    { path: '/forecast-generator', labelKey: 'nav.forecastGenerator', staffOnly: true },
    { path: '/safety-stock-generator', labelKey: 'nav.safetyStockGenerator', staffOnly: true },
    { path: '/search-analytics', labelKey: 'nav.searchAnalytics', staffOnly: true },
    { path: '/store-activity', labelKey: 'nav.storeActivity', staffOnly: true },
    { path: '/store-data-health', labelKey: 'nav.storeDataHealth', staffOnly: true },
  ],
};

/**
 * The sidebar this store sees: Forecast waits for the store's first forecast
 * (`utils/syncGate.forecastsMissing`).
 *
 * @param {{ forecastOffered: boolean }} viewer
 */
export function sidebarMenu({ forecastOffered }) {
  return SIDEBAR.filter((entry) => forecastOffered || !entry.needsForecast);
}

/** The screens a sidebar section counts as current on. */
export function sectionPaths(section) {
  const paths = section.links.map((link) => link.path);
  return section.hub ? [section.hub, ...paths] : paths;
}

/**
 * The user menu this viewer sees: the store's screens only while a store is
 * selected, since each of them is that store's page, and billing only where
 * the store has it (`billing/billingOffered`); staff tools only for staff —
 * each still marked `staffOnly`, so the menu can set them apart.
 *
 * @param {{ isStaff: boolean, store: object | null }} viewer  `store` is the selected store.
 */
export function userMenu({ isStaff, store }) {
  return {
    store: store && {
      profile: USER.store.profile,
      links: USER.store.links.filter((link) => billingOffered(store) || !link.needsBilling),
    },
    userProfile: USER.userProfile,
    tools: USER.tools.filter((link) => isStaff || !link.staffOnly),
  };
}
